import { MatSnackBar, MatDialog } from '@angular/material';
import { ErrorType } from '@models/error-type';
import { ConfirmDialogComponent } from '@shared/notification/confirm-dialog/confirm-dialog.component';
import { SuccessComponent } from '@shared/notification/success-snackbar/success.component';
import { ErrorComponent } from '@shared/notification/error-snackbar/error.component';
import { ErrorDialogComponent } from '@shared/notification/error-dialog/error-dialog.component';
import { LocalizationService } from './localization.service';
import { OkDialogComponent } from '@shared/notification/ok-dialog/ok-dialog.component';
import * as i0 from "@angular/core";
import * as i1 from "@angular/material/dialog";
import * as i2 from "@angular/material/snack-bar";
import * as i3 from "./localization.service";
var NotificationService = /** @class */ (function () {
    function NotificationService(dialog, snackBar, localizationService) {
        this.dialog = dialog;
        this.snackBar = snackBar;
        this.localizationService = localizationService;
        this.dialogOptionDefaults = {
            data: null
        };
    }
    /**
     * Displays a success message along the bottom of the screen to let the user know that some action succeeded.
     * @param messageKey The key from the language file to use for the message.
     * @param duration The number of milliseconds the notification will be displayed for.  Default: 4000.
     */
    NotificationService.prototype.successSnackBarKey = function (messageKey, duration) {
        var _this = this;
        if (duration === void 0) { duration = 4000; }
        this.localizationService.translate(messageKey).subscribe({
            next: function (message) { return _this.successSnackBar(message, duration); }
        });
    };
    /**
     * Show a small popup at the bottom of the screen with a short message for an amount of time.
     * @param message The text of the message that will be displayed.
     * @param duration The amount of time in ms the message will be shown.  Default: 4000.
     */
    NotificationService.prototype.successSnackBar = function (message, duration) {
        if (duration === void 0) { duration = 4000; }
        this.snackBar.openFromComponent(SuccessComponent, {
            duration: duration,
            data: {
                message: message
            }
        });
    };
    NotificationService.prototype.errorSnackBar = function (message) {
        this.snackBar.openFromComponent(ErrorComponent, {
            duration: 4000,
            data: {
                message: [message]
            }
        });
    };
    NotificationService.prototype.confirmSnackBar = function (message) {
        this.snackBar.openFromComponent(SuccessComponent, {
            verticalPosition: 'top',
            horizontalPosition: 'left',
            panelClass: 'confirmSnackBar',
            data: {
                message: message,
                confirm: true
            }
        });
    };
    NotificationService.prototype.openSnackBar = function (message, component) {
        this.snackBar.openFromComponent(component, {
            duration: 4000,
            data: {
                message: message
            }
        });
    };
    NotificationService.prototype.errorDialogKey = function (messageKey, errorType) {
        var _this = this;
        if (errorType === void 0) { errorType = ErrorType.SYSTEM; }
        this.localizationService.translate(messageKey).subscribe(function (message) {
            _this.errorDialog([message], errorType);
        });
    };
    NotificationService.prototype.errorDialog = function (message, errorType) {
        if (errorType === void 0) { errorType = ErrorType.SYSTEM; }
        if (errorType === ErrorType.VALIDATION) {
            this.openSnackBar(message, ErrorComponent);
        }
        if (errorType === ErrorType.SYSTEM) {
            var options = Object.assign(this.dialogOptionDefaults, {
                data: { message: message }
            });
            this.dialog.open(ErrorDialogComponent, options);
        }
    };
    /**
     * Shows a Confirm Dialog.  That is, a popup message with a "Confirm" and "Cancel" buttons that can be used to ask
     * the user if they are sure they'd like to continue with an action.  Example use:
     * ```
     *     this.notificationService.confirmDialog({
     *        width: '400px',
     *        data: {
     *          messageKey: 'Notifications.ConfirmRemoveMessage'
     *          params: {
     *             testName: selectedTest.title,
     *             userName: `${firstName} ${lastName}`
     *          }
     *        }
     *      })
     *      .afterClosed()
     *      .subscribe(isConfirmed => {
     *        if (isConfirmed) {
     *          this.store.deleteCertification(certificationID);
     *        }
     *      });
     *    }
     *
     * ```
     * The `key` parameters will be used to look up values in the currently selected language.
     * The dialog will look for the following parameters in `data`:
     * - titleKey: The key for the dialog title.
     * - titleText: The string to display as the title.
     * - messageKey: The key for the body of the dialog.
     * - message: the string that will be displayed in the body of the dialog.
     * - confirmText: The string that will be displayed on the `Confirm` button, "Confirm" by default.
     * - cancelText: The string that will be displayed on the `Cancel` button, "Cancel" by default.
     * - params: These are optional parameters that will be used with the keys if the localized strings have parameters in them.
     *
     * At the very least `data.message` or `data.messageKey` must be defined.
     * @param options The options for the dialog.
     */
    NotificationService.prototype.confirmDialog = function (options) {
        options = Object.assign(this.dialogOptionDefaults, options);
        return this.dialog.open(ConfirmDialogComponent, options);
    };
    NotificationService.prototype.okDialog = function (options) {
        options = Object.assign(this.dialogOptionDefaults, options);
        return this.dialog.open(OkDialogComponent, options);
    };
    NotificationService.prototype.showErrors = function (error) {
        var err = error.error;
        var messages = [];
        for (var i = 0; i < err.errors.length; i++) {
            messages.push(err.errors[i].message);
        }
        this.errorDialog(messages, err.type);
    };
    NotificationService.prototype.toastSuccessFailObserver = function (successKey) {
        var _this = this;
        return {
            next: function (_) {
                return _this.localizationService.translate("Notifications." + successKey).subscribe({
                    next: function (message) { return _this.successSnackBar(message); }
                });
            },
            error: function (error) { return _this.showErrors(error); }
        };
    };
    NotificationService.ngInjectableDef = i0.defineInjectable({ factory: function NotificationService_Factory() { return new NotificationService(i0.inject(i1.MatDialog), i0.inject(i2.MatSnackBar), i0.inject(i3.LocalizationService)); }, token: NotificationService, providedIn: "root" });
    return NotificationService;
}());
export { NotificationService };
